import React, { useRef, useEffect, useState } from 'react';
import Chart from 'chart.js/auto';
import { supabaseClient } from '../../services/supabase';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import { standardLegend, standardTitle } from '../../utils/chartFormatting';

const formatSupabaseError = (err: unknown) => {
  if (!err || typeof err !== 'object') return 'Unknown error';
  const anyErr = err as { message?: string; details?: string; hint?: string; code?: string };
  const parts = [anyErr.message, anyErr.details, anyErr.hint, anyErr.code].filter(Boolean);
  return parts.length ? parts.join(' | ') : 'Unknown error';
};

const buildGeofipsVariants = (geofips: number) => {
  const raw = String(geofips).trim();
  const unpadded = raw.replace(/^0+/, '') || '0';
  const padded = raw.padStart(7, '0');
  const variants = new Set<string | number>([raw, unpadded, padded]);
  const numeric = Number(unpadded);
  if (!Number.isNaN(numeric)) {
    variants.add(numeric);
  }
  return Array.from(variants);
};

interface HealthLowBirthWeightChartProps {
  location: string;
}

export const HealthLowBirthWeightChart: React.FC<HealthLowBirthWeightChartProps> = ({ location }) => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [locationValue, setLocationValue] = useState<number | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!location) return;

      try {
        setLoading(true);
        setError(null);

        // Get geofips for the location
        const geofips = await supabaseLocationService.getGeofips(location);
        console.log(`🔍 HealthLowBirthWeightChart: Location="${location}", geofips=${geofips}`);
        if (!geofips) {
          throw new Error(`Could not find geofips for location: ${location}`);
        }

        // Determine geography type
        const geofipsVariants = buildGeofipsVariants(geofips);

        const { data: locationData } = await supabaseClient
          .from('population')
          .select('geography_type')
          .in('geofips', geofipsVariants)
          .limit(1)
          .maybeSingle();

        let geographyType = locationData?.geography_type || 'City';
        console.log(`🔍 HealthLowBirthWeightChart: geography_type=${geographyType}`);

        // Fetch location data - try multiple geography types if needed
        let locationCES4 = null;
        let locationError = null;
        
        // Try the determined geography type first
        const { data: ces4Data, error: ces4Error } = await supabaseClient
          .from('calenviroscreen_4_0_aggregated')
          .select('low_birth_weight')
          .in('geofips', geofipsVariants)
          .eq('geography_type', geographyType)
          .limit(1)
          .maybeSingle();
        
        locationCES4 = ces4Data;
        locationError = ces4Error;
        
        // If not found, try other geography types
        if (!locationCES4 && !locationError) {
          for (const geoType of ['City', 'Place', 'County']) {
            if (geoType !== geographyType) {
              const { data: altData, error: altError } = await supabaseClient
                .from('calenviroscreen_4_0_aggregated')
                .select('low_birth_weight')
                .in('geofips', geofipsVariants)
                .eq('geography_type', geoType)
                .limit(1)
                .maybeSingle();
              
              if (altData && !altError) {
                locationCES4 = altData;
                geographyType = geoType;
                console.log(`🔍 HealthLowBirthWeightChart: Found data with geography_type=${geoType}`);
                break;
              }
            }
          }
        }

        console.log(`🔍 HealthLowBirthWeightChart: Location data:`, locationCES4, `Error:`, locationError);

        if (locationError) {
          console.error('Error fetching location low birth weight data:', locationError);
          throw locationError;
        }

        // Fetch California state data
        const { data: californiaCES4, error: californiaError } = await supabaseClient
          .from('calenviroscreen_4_0_aggregated')
          .select('low_birth_weight')
          .in('geofips', buildGeofipsVariants(6))
          .eq('geography_type', 'State')
          .limit(1)
          .maybeSingle();

        if (californiaError) {
          console.error('Error fetching California low birth weight data:', californiaError);
          throw californiaError;
        }

        const locValue = locationCES4?.low_birth_weight ?? null;
        const caValue = californiaCES4?.low_birth_weight ?? null;

        console.log(`🔍 HealthLowBirthWeightChart: Location value=${locValue}, California value=${caValue}`);

        setLocationValue(locValue);

        // Create chart - show location data even if California data is missing
        if (locValue !== null) {
          const createChart = (attempt = 0) => {
            if (!chartRef.current) {
              if (attempt < 3) {
                setTimeout(() => createChart(attempt + 1), 100);
              }
              return;
            }

            const ctx = chartRef.current.getContext('2d');
            if (!ctx) {
              return;
            }

            // Destroy existing chart if it exists
            if (chartInstance.current) {
              chartInstance.current.destroy();
            }

            // Prepare data - include California only if available
            const labels = caValue !== null ? [location, 'California Average'] : [location];
            const data = caValue !== null ? [locValue, caValue] : [locValue];
            const backgroundColor = caValue !== null 
              ? ['#3b82f6', '#10b981']  // Blue for location, Green for California
              : ['#3b82f6'];  // Just blue for location
            const borderColor = caValue !== null
              ? ['#2563eb', '#059669']
              : ['#2563eb'];

            chartInstance.current = new Chart(ctx, {
              type: 'bar',
              data: {
                labels: labels,
                datasets: [
                  {
                    label: 'Low Birth Weight Rate',
                    data: data,
                    backgroundColor: backgroundColor,
                    borderColor: borderColor,
                    borderWidth: 2
                  }
                ]
              },
              options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: standardLegend,
                  title: standardTitle(
                    caValue !== null
                      ? `Low Birth Weight - ${location} vs California`
                      : `Low Birth Weight - ${location}`
                  ),
                  tooltip: {
                    callbacks: {
                      label: function(context) {
                        const value = context.parsed.y;
                        const label = context.label;
                        return `${label}: ${value !== null ? value.toFixed(2) : 'N/A'}%`;
                      }
                    }
                  }
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    title: {
                      display: true,
                      text: 'Percent Low Birth Weight'
                    }
                  }
                }
              }
            });
          };

          createChart();
        }
      } catch (err) {
        console.error('Error in HealthLowBirthWeightChart:', err);
        const message = err instanceof Error ? err.message : formatSupabaseError(err);
        setError(`Failed to load low birth weight data: ${message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [location]);

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '20px' }}>Loading low birth weight data...</div>;
  }

  if (error) {
    return <div style={{ textAlign: 'center', padding: '20px', color: '#ef4444' }}>Error: {error}</div>;
  }

  if (locationValue === null) {
    return <div style={{ textAlign: 'center', padding: '20px' }}>No low birth weight data available for this location.</div>;
  }

  return (
    <div style={{ position: 'relative', height: '400px' }}>
      <canvas ref={chartRef} />
    </div>
  );
};

